import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { getTheme, mergeStyles, Spinner, SpinnerSize } from '@fluentui/react'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { useTranslation } from 'react-i18next'
import { Destination, GraphError } from '../../context/AxiosContext'
import useEntity from '../../hooks/useEntity'
import { DocumentOverviewItemDto } from '../../config/documentConfig'
import {
    driveItemOverviewProperties,
    formatDocumentUrl,
    mapDriveItemToOverviewItemDto,
    resolveOpenUrl,
    shouldOpen,
} from '../../helpers/documentHelper'
import IconValue from '../IconValue/IconValue'
import DocumentName from '../Render/DocumentName'
import { makeStyles, tokens } from '@fluentui/react-components'
import useDocumentsOpen from '../../hooks/document/useDocumentsOpen'

export const useStyles = makeStyles({
    wrapper: {
        alignSelf: 'start',
        margin: tokens.spacingHorizontalS,
    },
})

export default function DocumentLink({ id, document }: { id: string; document?: DocumentOverviewItemDto }): ReactElement {
    const documentsOpen = useDocumentsOpen()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const [attachment] = useEntity<DocumentOverviewItemDto, DriveItem, GraphError>(
        id => ({
            url: `/items/${id}`,
            method: 'GET',
            baseURL: Destination.Documents,
            params: { $select: driveItemOverviewProperties.join(',') },
        }),
        document ? null : id,
        response => mapDriveItemToOverviewItemDto(response.data),
    )

    const finalAttachment = useMemo(() => document || attachment?.data, [document, attachment])

    if (attachment.rejected) {
        return (
            <IconValue
                iconName="Warning"
                value={translate('base.empty')}
                wrapperClassName={mergeStyles({ color: getTheme().semanticColors.warningIcon })}
            />
        )
    }

    if (!document && (attachment.loading || !attachment.loaded)) {
        return <Spinner label={translate('base.loading')} size={SpinnerSize.xSmall} labelPosition="right" />
    }

    return (
        <DocumentName
            id={finalAttachment?.id}
            isLink={true}
            name={finalAttachment?.name}
            url={shouldOpen(documentsOpen, false) ? resolveOpenUrl(attachment.data, documentsOpen) : formatDocumentUrl(attachment.data, undefined)}
            nodeType={finalAttachment?.type}
            iconValueProps={{ wrapperClassName: styles.wrapper }}
        />
    )
}
